import {React, Suspense, useHotkeys, observer, classNames, useLocalStore} from '@api'
import {ContextMenu2ChildrenProps} from '@api'
import {Toaster} from '@blueprintjs/core'
import {
  LoadingIndicator,
  ConfirmationDialog, useStores, UserNotificationSettingsDialog, EditWorkspacePresetDialog, FilesToPasteIntoCatalogDialog, RightSidebar, PresentationModeContent
} from '@design-board/components'
import {Snackbar} from '@material-ui/core'
import {DragDropEventHandlers, FlexLayout, globalStores, site, StoresInstance, ui} from '@stores'
import {CatalogDropTargets} from '@stores/dragDrop'
import {strings} from '@stores/StringsStore'
import {QrCodeDialog} from 'centric-ui-components'
import {action, autorun, computed, observable, runInAction} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {Fragment, useContext, useEffect} from 'react'
import * as css from './CatalogRoot.css'

const NullComponent = () => null

const PrefetchItemDetails = React.lazy(() => import('@modules/CatalogItemDetails').then(() => ({default: NullComponent})))
// const PrefetchTableView = React.lazy(() => import(/* webpackChunkName: "TableView" */ '@modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/TableView').then(() => ({default: NullComponent})))

export const CatalogRootStoreContext = React.createContext<CatalogRootStore>(undefined)
export const useCatalogRootStore = () => useContext(CatalogRootStoreContext)

export class CatalogRootStore {
  constructor(stores: StoresInstance) {
    this.providerStores = stores
  }

  _toDispose: IDisposer[] = []

  @action dispose = () => {
    this._toDispose.forEach(f => f())
  }

  registerEffects = () => {
    this._toDispose.push(
      autorun(() => {
        const {stores} = this

        if (stores) {
          runInAction(() => {
            const {catalog} = this
            this.dragDropEvents = new DragDropEventHandlers(catalog, {target: CatalogDropTargets.root})
            catalog.componentStores.root = this
            stores.catalog.componentStores.root = this
          })
        }
      })
    )
  }

  @observable.ref scrollPanel: HTMLDivElement
  @action setScrollPanel = r => this.scrollPanel = r

  providerStores: StoresInstance
  dragDropEvents: DragDropEventHandlers
  @observable touchStartPosX
  @observable touchStartPosY
  @observable touchEndPosX
  @observable touchEndPosY

  @action('Click on GroupByCatalog - clear selection when in card view') onClick = (e: React.MouseEvent | React.TouchEvent) => {
    const {stores} = this
    if (!stores) return

    const {catalog, catalog: {settings: {viewMode}, selection}} = stores
    if (viewMode == 'card') {
      selection.clear()
      catalog.focusedCatalogItemId = null
    }
    catalog.hideCatalogPopover()

    e.stopPropagation()
  }

  @action onTouchMove = (e: any) => {
    e.stopPropagation()
  }

  @action(`Clear selection on touch end if they haven't dragged`) onTouchEnd = (e: any) => {
    this.touchEndPosX = e.changedTouches[0].clientX
    this.touchEndPosY = e.changedTouches[0].clientY
    if (this.touchEndPosX === this.touchStartPosX && this.touchEndPosY === this.touchStartPosY) {
      const {catalog, catalog: {settings: {viewMode}, selection}} = this
      if (viewMode == 'card') {
        selection.clear()
        catalog.focusedCatalogItemId = null
      }
    }
    this.clearCoordinates()
  }

  @action(`Watch touch start and end positions to determine if click vs drag`) onTouchStart = (e: React.TouchEvent) => {
    this.touchStartPosX = e.touches[0].clientX
    this.touchStartPosY = e.touches[0].clientY
  }

  @action(`Clear touch coordinate tracking `) clearCoordinates() {
    this.touchStartPosX = 0
    this.touchStartPosY = 0
    this.touchEndPosX = 0
    this.touchEndPosY = 0
  }

  @computed get view(): 'pdf' | 'catalog' {
    const {catalog: {pdfConfig, isShowingClientPDF}} = this
    return pdfConfig && isShowingClientPDF ? 'pdf' : 'catalog'
  }

  @computed get catalog() {
    return this.stores?.catalog
  }

  @computed get stores(): StoresInstance {
    const {providerStores, providerStores: {flexLayout, flexLayout: {isMaximized}}} = this

    if (!isMaximized) {
      if (providerStores?.isGlobalStore) return null
      return providerStores
    } else {
      return flexLayout.activeCatalogRoot?.storesToInject
    }
  }

  @computed get dontRoundCardCorners() {
    return this.catalog.cardSpacingAsMultiplier < .5
  }

  @computed get dataAttributes() {
    const {stores} = this

    if (!stores?.catalog?.hasLoadedConfig) return {}

    const {catalog: {features, settings: {align, verticalAlign, showCardLabels, showInlineCatalogItemsFirst, levelOfDetail, borders, groupBy: {flatModeColumns}, imageRatio}, seasonSelector, isProductView, groupBy: {hierarchyGroups, settings: {groupView}, hierarchy}}} = stores,
      {view, dontRoundCardCorners} = this

    return {
      'data-align': align,
      'data-valign': verticalAlign,
      'data-lod': levelOfDetail.toString(),
      'data-image-ratio': imageRatio,
      'data-is-product-view': isProductView,
      'data-group-depth': hierarchy.length,
      'data-hierarchy-dirty': seasonSelector?.dirty,
      'data-show-group-headers': features.groupBy?.showGroupLabel === true,
      'data-view': view,
      'data-no-card-borders': features.card?.noBorders,
      'data-catalog-view': groupView == 'pivot' && hierarchy.length <= 1 ? 'card' : groupView,
      'data-columns': flatModeColumns,
      'data-orientation': (_.isEmpty(hierarchyGroups) ? null : hierarchyGroups[0]?.orientation) ?? 'horizontal',
      'data-card-borders': borders.cardBorders === true,
      'data-card-grid-lines': borders.gridLines,
      'data-show-inline-cards-first': showInlineCatalogItemsFirst,
      'data-show-card-labels': showCardLabels,
      'data-round-corners': !dontRoundCardCorners
    }
  }
}

export const CatalogRoot = observer<{ maximizedRoot?: boolean, className?: string }>(function CatalogRoot({className, maximizedRoot, children}) {
  const providerStores = useStores(),
    store = useLocalStore(() => new CatalogRootStore(providerStores)),
    {
      onClick, onTouchEnd, onTouchMove, onTouchStart, dragDropEvents, catalog,
      stores, providerStores: {flexLayout, flexLayout: {isMaximized}},
      dataAttributes
    } = store,
    {activeTab} = flexLayout,
    catalogTab = maximizedRoot && !isMaximized ? null : maximizedRoot ? flexLayout.activeCatalogRoot?.tab : stores?.vipTabs?.catalog,
    ignoreHotkeys = !maximizedRoot && isMaximized

  // useEffect(() => {
  //   console.log(`Mount CatalogRoot`, { id: store.stores?._id})
  //   return () => {
  //     console.log(`Unmount CatalogRoot`, {id: store.stores?._id})
  //   }
  // }, [])

  useEffect(() => store.registerEffects(), [])
  // useEffect(() => {
  //   console.log(`Mount CatalogRoot - ${stores._id} - ${maximizedRoot}`)
  //   return () => console.log(`Unmount CatalogRoot - ${stores._id} - ${maximizedRoot}`)
  // }, [])

  useHotkeys('esc', (e) => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab?.storesToInject?.catalog?.selection) return

    const {catalog: {selection}, sidebar: {right}} = catalogTab.storesToInject
    const target = e.target as HTMLElement
    // Did this come from a dialog?
    if (!target.closest('[role="document"]')) {
      selection.clear()
    }

    if (right.open) {
      right.onKeyDown(e as any)
    }
  }, [ignoreHotkeys, activeTab, catalogTab])

  useHotkeys('shift+left, left', e => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab?.storesToInject) return
    catalogTab?.storesToInject?.catalog?.navigateToPreviousCatalogItem(e)
  }, [ignoreHotkeys, catalogTab, activeTab])

  useHotkeys('enter', (e) => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab?.storesToInject) return
    const {sidebar: {right}, catalog} = catalogTab.storesToInject
    right.updateState({open: true, path: [{where: [{value: catalog.focusedCatalogItemId}]}]})
  }, [ignoreHotkeys, catalogTab, activeTab])

  // useHotkeys('d', () => {
  //   right.updateState({open: !right.open, path: [{where: [{value: catalog.focusedCatalogItemId}]}]})
  // }, [catalogTab])

  useHotkeys('cmd+s', e => {
    if (ignoreHotkeys) return
    e.preventDefault()
    stores?.reports.currentReport?.updateSettings(globalStores.catalog.reportSettings)
  }, [ignoreHotkeys, stores])

  useHotkeys('shift+right,right', e => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab?.storesToInject) return
    catalogTab.storesToInject.catalog.navigateToNextCatalogItem(e)
  }, [ignoreHotkeys, catalogTab, activeTab])

  // useHotkeys('cmd+shift+l', e => {
  //   catalog.settings.flexLayout.enabled = !catalog.settings.flexLayout.enabled
  // }, [catalogTab])

  useHotkeys('cmd+shift+o', e => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard') return
    flexLayout.create.newCatalogTab({view: 'card'})
  }, [ignoreHotkeys, activeTab])

  useHotkeys('del,cmd+backspace', (e) => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab) return
    catalogTab.storesToInject.catalog.selection.askDeleteSelectedItems()
  }, [ignoreHotkeys, catalogTab, activeTab])

  useHotkeys('s', (e) => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab) return
    if (!site.isShowingPopover) {
      catalogTab.storesToInject.catalog.navigateToFocusedCatalogItemSeason()
    }
  }, [ignoreHotkeys, catalogTab, activeTab])

  useHotkeys('i', (e) => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab) return
    if (!site.isShowingPopover) {
      catalogTab.storesToInject.catalog.selection.toggleSelectedInlineDetails()
    }
  }, [ignoreHotkeys, catalogTab, activeTab])

  useHotkeys('f', (e) => {
    if (ignoreHotkeys || activeTab?.component === 'whiteboard' || !catalogTab) return
    if (!site.isShowingPopover) {
      catalogTab.storesToInject.catalog.focusedCatalogItemStore?.showFullScreenImage?.()
    }
  }, [ignoreHotkeys, catalogTab, activeTab])

  //useHotkeys('f', catalog.toggleFullScreenView)

  return (
    <CatalogRootStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           {...(dragDropEvents?.events ?? {})}
           onClick={site.touchDevice ? undefined : onClick}
           onTouchEnd={site.touchDevice ? onTouchEnd : undefined}
           onTouchMove={site.touchDevice ? onTouchMove : undefined}
           onTouchStart={site.touchDevice ? onTouchStart : undefined}
           {...dataAttributes}
           data-type='CatalogRoot'
           data-context-menu={providerStores.contextMenu.isShowing}
           data-loading={stores?.catalog?.loading === true && !stores.catalog.isShowingClientPDF}
           style={catalog?.rootStyle}
      >
        {children || <CatalogRootContentContainer/>}

        <CatalogDialogs/>
        {catalogTab?.storesToInject?.sidebar?.right?.showImageFullscreen && <RightSidebar/>}
      </div>
    </CatalogRootStoreContext.Provider>)
})

export const CatalogDialogs = observer(function CatalogDialogs() {
  const stores = useStores(),
    {isInlineDetailsStore, catalog, catalog: {filesToMaybePaste, presentation, dialogs: {snackbar, onCloseSnackbar, confirmationPopup, onCancelPopup, onConfirmPopup, showQrCodeModal}}} = stores

  return (<Fragment>
    <UserNotificationSettingsDialog/>

    <EditWorkspacePresetDialog/>

    <Snackbar
      anchorOrigin={snackbar.align}
      onClose={onCloseSnackbar}
      open={snackbar.show}
      autoHideDuration={ui.settings.AUTOHIDE_SNACKBAR_DURATION}
      classes={{root: classNames(css.snackbarRoot)}}
      ContentProps={{
        'aria-describedby': 'error-msg'
      }}
      message={<span id='snack-msg'>{snackbar.message}</span>}
    />
    <FilesToPasteIntoCatalogDialog/>

    {confirmationPopup.show && (
      <ConfirmationDialog
        {...confirmationPopup}
        openConfirmDialog
        onCancel={onCancelPopup}
        onClickConfirm={onConfirmPopup}
        cancelLabel={'CANCEL'}
        confirmationLabel={['DELETE_PRODUCT', 'DELETE_PRODUCTS', 'DELETE_IMAGE', 'UPLOAD_IMAGE', 'REORDER_IMAGE', 'UPLOAD_PRODUCT', 'SYNC_PRODUCT', 'DELETE_TAG', 'UPDATE_TAG', 'REPLACE_PRODUCT'].indexOf(confirmationPopup.operation) != -1
                           ? confirmationPopup.operation.split('_')[0]
                           : confirmationPopup?.confirmationLabel ?? 'DUPLICATE'}
      />
    )}
    {showQrCodeModal && <QrCodeDialog stores={stores}/>}

    {!isInlineDetailsStore && presentation && catalog.isGroupedView && <PresentationModeContent />}
  </Fragment>)
})

const CatalogRootContentContainer = observer(function CatalogRootContentContainer() {
  const store = useCatalogRootStore(),
    {
      providerStores,
      stores
    } = store

  // useEffect(() => {
  //   console.log(`Mount CatalogRootContentContainer`)
  //   return () => {
  //     console.log(`Unmount CatalogRootContentContainer`)
  //   }
  // }, [])

  // useEffect(() => {
  //   console.log(`Mount CatalogRootContentContainer`)
  //   return () => console.log(`Unmount CatalogRootContentContainer`)
  // })
  return (
    <div className={classNames(css.container)}>
      <Suspense fallback={null}>
        <PrefetchItemDetails/>
        {/*<PrefetchTableView/>*/}
      </Suspense>
      <Suspense fallback={<LoadingIndicator className={css.loading} text={stores?.catalog?.loadingState?.loadingMessage ?? strings.renderingCatalog(stores?.catalog ?? providerStores?.catalog)}/>}>

        {/*{(flexLayout.maximizedTab?.component == 'catalog') && <Suspense fallback={null}><FilterChipsToolbar className={css.secondaryToolbar}/></Suspense>}*/}
        {!globalStores?.users?.user?.includesRoles ? <LoadingIndicator text={`Loading User Roles...`} /> : <FlexLayout className={css.flexLayoutRoot}/>}
      </Suspense>
    </div>
  )
})

export const CatalogContextMenuContext = React.createContext<ContextMenu2ChildrenProps>(undefined)
export const useCatalogContextMenu = () => useContext(CatalogContextMenuContext)



// WEBPACK FOOTER //
// ./src/modules/catalog/CatalogRoot.tsx